import { api } from './api';
import { MediaAsset, CreateAssetData, UpdateAssetData } from '../store/assetsStore';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

const API_ENDPOINTS = {
  assets: '/assets',
  asset: (id: string) => `/assets/${id}`,
  upload: '/assets/upload',
  download: (id: string) => `/assets/${id}/download`,
  link: (id: string) => `/assets/${id}/link`,
  duplicate: (id: string) => `/assets/${id}/duplicate`,
  move: '/assets/move',
  tag: '/assets/tag',
  deleteMultiple: '/assets/bulk-delete',
};

function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export class AssetsService {
  // Get all assets
  async getAssets(): Promise<MediaAsset[]> {
    try {
      const response = await api.get<ApiResponse<MediaAsset[]>>(API_ENDPOINTS.assets);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch assets');
      return response.data.data.map(this.transformAsset);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      throw new Error('Failed to fetch assets. Please try again.');
    }
  }

  // Upload asset
  async uploadAsset(data: CreateAssetData): Promise<MediaAsset> {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.tags) {
        formData.append('tags', JSON.stringify(data.tags));
      }
      if (data.altText) {
        formData.append('altText', data.altText);
      }
      if (data.folder) {
        formData.append('folder', data.folder);
      }

      const response = await api.post<ApiResponse<MediaAsset>>(API_ENDPOINTS.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to upload asset');

      toast.success('Asset uploaded successfully');
      return this.transformAsset(response.data.data);
    } catch (error) {
      console.error('Failed to upload asset:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to upload asset. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Update asset
  async updateAsset(id: string, data: UpdateAssetData): Promise<MediaAsset> {
    try {
      const response = await api.patch<ApiResponse<MediaAsset>>(API_ENDPOINTS.asset(id), data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update asset');

      toast.success('Asset updated successfully');
      return this.transformAsset(response.data.data);
    } catch (error) {
      console.error('Failed to update asset:', error);
      let message = 'Failed to update asset. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Asset not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Delete asset
  async deleteAsset(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.asset(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete asset');

      toast.success('Asset deleted successfully');
    } catch (error) {
      console.error('Failed to delete asset:', error);
      let message = 'Failed to delete asset. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Asset not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Delete multiple assets
  async deleteAssets(ids: string[]): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(API_ENDPOINTS.deleteMultiple, { ids });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete assets');

      toast.success(`${ids.length} asset(s) deleted successfully`);
    } catch (error) {
      console.error('Failed to delete assets:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to delete assets. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Download asset
  async downloadAsset(id: string): Promise<void> {
    try {
      const response = await api.get<Blob>(API_ENDPOINTS.download(id), {
        responseType: 'blob',
      });
      
      // Get filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : 'download';

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Asset downloaded successfully');
    } catch (error) {
      console.error('Failed to download asset:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to download asset. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Copy asset link
  async copyAssetLink(id: string): Promise<void> {
    try {
      const response = await api.get<ApiResponse<{ link: string }>>(API_ENDPOINTS.link(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to get asset link');

      await navigator.clipboard.writeText(response.data.data.link);
      toast.success('Link copied to clipboard');
    } catch (error) {
      console.error('Failed to copy link:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to copy link. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Duplicate asset
  async duplicateAsset(id: string): Promise<MediaAsset> {
    try {
      const response = await api.post<ApiResponse<MediaAsset>>(API_ENDPOINTS.duplicate(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to duplicate asset');

      toast.success('Asset duplicated successfully');
      return this.transformAsset(response.data.data);
    } catch (error) {
      console.error('Failed to duplicate asset:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to duplicate asset. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Move assets
  async moveAssets(ids: string[], folder: string): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(API_ENDPOINTS.move, { ids, folder });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to move assets');

      toast.success(`${ids.length} asset(s) moved successfully`);
    } catch (error) {
      console.error('Failed to move assets:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to move assets. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Tag assets
  async tagAssets(ids: string[], tags: string[]): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(API_ENDPOINTS.tag, { ids, tags });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to tag assets');

      toast.success(`${ids.length} asset(s) tagged successfully`);
    } catch (error) {
      console.error('Failed to tag assets:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to tag assets. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Helper: normalize asset data
  private transformAsset(asset: any): MediaAsset {
    return {
      id: asset.id || asset._id,
      filename: asset.filename || '',
      originalName: asset.originalName || '',
      mimeType: asset.mimeType || '',
      fileSize: asset.fileSize || 0,
      cdnUrl: asset.cdnUrl || '',
      thumbnailUrl: asset.thumbnailUrl,
      tags: asset.tags || [],
      altText: asset.altText,
      folder: asset.folder || 'root',
      uploadedBy: asset.uploadedBy || '',
      teamId: asset.teamId || '',
      usageCount: asset.usageCount || 0,
      usedIn: asset.usedIn || [],
      createdAt: asset.createdAt || new Date().toISOString(),
      updatedAt: asset.updatedAt || new Date().toISOString(),
    };
  }
}

// Singleton instance
export const assetsService = new AssetsService();

